import { NAV_MODE_LISTENER, resolveNavMode } from './siteNavMode';

/**
 * Shared Listening / Talking tab ids for Dashboard tools and Your profile.
 * Deep-links: /profile?tab=listening | /profile?tab=talking
 *             /dashboard?tab=listening | /dashboard?tab=talking
 */

export const PROFILE_TAB_LISTENING = 'listening';
export const PROFILE_TAB_TALKING = 'talking';

/**
 * @param {string} [tab]
 * @returns {boolean}
 */
export const isProfileTab = tab => tab === PROFILE_TAB_LISTENING || tab === PROFILE_TAB_TALKING;

/**
 * Tab from `/profile/:tab` or `?tab=`.
 *
 * @param {string} [search]
 * @param {string} [paramsTab]
 * @returns {'listening'|'talking'|null}
 */
export const parseRequestedTab = (search, paramsTab) => {
  if (isProfileTab(paramsTab)) {
    return paramsTab;
  }
  const fromQuery = new URLSearchParams(search || '').get('tab');
  return isProfileTab(fromQuery) ? fromQuery : null;
};

/**
 * Default tab: Listening if they have a profile or still owe setup, else Talking.
 *
 * @param {Object} [params]
 * @param {boolean} [params.hasListeningProfile]
 * @param {boolean} [params.showIncompleteProfile]
 * @returns {'listening'|'talking'}
 */
export const defaultRoleTab = ({
  hasListeningProfile = false,
  showIncompleteProfile = false,
} = {}) =>
  hasListeningProfile || showIncompleteProfile ? PROFILE_TAB_LISTENING : PROFILE_TAB_TALKING;

/**
 * Never show Listening | Talking pairs. Mode comes from the menu Switch
 * (page role, else stored preferred). One pane only.
 *
 * @returns {boolean}
 */
export const showRoleTabChrome = () => false;

/**
 * Tabs to render. Both → both. Listen-only → Listening only. Talk-only → Talking only.
 *
 * @param {Object} [params]
 * @param {boolean} [params.showListening]
 * @param {boolean} [params.showTalking]
 * @returns {Array<'listening'|'talking'>}
 */
export const visibleRoleTabs = ({ showListening = false, showTalking = false } = {}) => {
  const tabs = [];
  if (showListening) {
    tabs.push(PROFILE_TAB_LISTENING);
  }
  if (showTalking) {
    tabs.push(PROFILE_TAB_TALKING);
  }
  return tabs;
};

/**
 * Which tool / profile tab to show.
 *
 * @param {Object} [params]
 * @param {boolean} [params.showListening]
 * @param {boolean} [params.showTalking]
 * @param {boolean} [params.hasListeningProfile]
 * @param {string} [params.requestedTab]
 * @param {string} [params.hash]
 * @param {'talker'|'listener'|null} [params.preferredMode]
 * @returns {'listening'|'talking'}
 */
export const resolveRoleTab = ({
  showListening = false,
  showTalking = false,
  hasListeningProfile = false,
  showIncompleteProfile = false,
  requestedTab = null,
  hash = '',
  preferredMode = null,
} = {}) => {
  if (showListening && showTalking) {
    if (hash === '#featured') {
      return PROFILE_TAB_LISTENING;
    }
    if (isProfileTab(requestedTab)) {
      return requestedTab;
    }
    if (preferredMode === 'talker') {
      return PROFILE_TAB_TALKING;
    }
    if (preferredMode === 'listener') {
      return PROFILE_TAB_LISTENING;
    }
    return defaultRoleTab({ hasListeningProfile, showIncompleteProfile });
  }
  if (showListening && !showTalking) {
    return PROFILE_TAB_LISTENING;
  }
  return PROFILE_TAB_TALKING;
};

/**
 * Current-mode profile label: My Talking Profile or My Listening Profile.
 *
 * @param {Object} [params]
 * @param {Object} [params.currentUser]
 * @param {boolean} [params.currentUserHasListings]
 * @param {Object} [params.currentUserOwnListing]
 * @param {Array} [params.ownListings]
 * @param {string} [params.currentPage]
 * @param {string} [params.search]
 * @param {string} [params.inboxTab]
 * @param {'talker'|'listener'} [params.mode]
 * @returns {string}
 */
export const profileMenuLabelId = (params = {}) => {
  const mode = params.mode || resolveNavMode(params);
  return mode === NAV_MODE_LISTENER
    ? 'SiteDrawer.myListeningProfile'
    : 'SiteDrawer.myTalkingProfile';
};

/**
 * NamedLink props for the single Profile page, optionally opening a tab.
 *
 * @param {'listening'|'talking'} [tab]
 * @param {Object} [to]
 * @returns {{ name: string, to?: Object }}
 */
export const yourProfileNamedLinkProps = (tab, to = {}) => {
  if (isProfileTab(tab)) {
    return {
      name: 'YourProfilePage',
      to: { ...to, search: `?tab=${tab}` },
    };
  }
  return { name: 'YourProfilePage', ...(Object.keys(to).length ? { to } : {}) };
};

/**
 * NamedLink props for the current-mode My Talking / My Listening Profile item.
 *
 * @param {Object} [params]
 * @returns {{ name: string, to?: Object }}
 */
export const profileMenuNamedLinkProps = (params = {}) => {
  const mode = params.mode || resolveNavMode(params);
  return yourProfileNamedLinkProps(
    mode === NAV_MODE_LISTENER ? PROFILE_TAB_LISTENING : PROFILE_TAB_TALKING
  );
};

/**
 * Old Account settings → Profile links now open Notifications & contact.
 *
 * @returns {{ name: string }}
 */
export const accountProfileNamedLinkProps = () => ({ name: 'ContactDetailsPage' });

/**
 * Talking edit is a query on /profile so the bare hub stays read-only.
 *
 * @param {string} [search]
 * @returns {boolean}
 */
export const isTalkingEditSearch = search =>
  new URLSearchParams(search || '').get('edit') === PROFILE_TAB_TALKING;

/**
 * NamedLink props for Edit Talking Profile.
 *
 * @returns {{ name: string, to: { search: string } }}
 */
export const talkingEditNamedLinkProps = () => ({
  name: 'YourProfilePage',
  to: { search: '?edit=talking' },
});

/**
 * Legacy talker schedule editor query. Bookmarks still redirect:
 * listeners go to listing availability, talk-only back to the hub.
 *
 * @param {string} [search]
 * @returns {boolean}
 */
export const isTalkerScheduleEditSearch = search =>
  new URLSearchParams(search || '').get('edit') === 'schedule';

/**
 * Legacy NamedLink props for the retired talker schedule editor.
 *
 * @returns {{ name: string, to: { search: string } }}
 */
export const talkerScheduleEditNamedLinkProps = () => ({
  name: 'YourProfilePage',
  to: { search: '?edit=schedule' },
});
